import React, { useEffect, useState } from "react";
import { Col, Row } from "reactstrap";
import { Button, Input, Select, DatePicker, message, Spin, Flex } from "antd";
import { CustomLabel } from "../../../utilities/utll";
import {
  DownCircleOutlined,
  FilterOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { apiRequest } from "../../../utilities/apiMethod";
import { useIrnStore } from "../../../store/useIrnstore";

export const AllPoForm = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [status, setStatus] = useState("All");

  const { getAllPos, allPos } = useIrnStore();
  const irnStatus = ["All", "Pending", "Partial", "Completed"];

  useEffect(() => {
    fetchPeriods();
  }, []);

  const fetchPeriods = async () => {
    try {
      setLoading(true);
      const response = await apiRequest("GET", "Period/getperiods");
      if (response.IsSuccess && response.Data) {
        const data = response.Data;
        setPeriods(data);
        const todayPeriod =
          data.find((p) => p.PeriodName === "Today") || data[0];

        if (todayPeriod) {
          const from = dayjs(todayPeriod.FromDate);
          const to = dayjs(todayPeriod.Todate);

          setSelectedPeriod(todayPeriod.PeriodId);
          setFromDate(from);
          setToDate(to);

          // Call API once automatically
          await getAllPos(
            from.format("YYYY-MM-DD"),
            to.format("YYYY-MM-DD"),
            status,
          );
        }
        messageApi.success(
          response.DisplayMessage || "Periods loaded successfully",
        );
      } else {
        if (response.ErrorMessage && response.ErrorMessage.length > 0) {
          response.ErrorMessage.forEach((err) => {
            messageApi.error(err);
          });
        } else {
          messageApi.error(response.DisplayMessage || "Failed to load periods");
        }
      }
    } catch (error) {
      console.error("Error fetching periods:", error);
      messageApi.error("An error occurred while fetching periods");
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodChange = (value) => {
    setSelectedPeriod(value);
    const selected = periods.find((p) => p.PeriodId === value);

    if (selected) {
      if (selected.PeriodName === "Custom") {
        // For custom, clear dates and let user select
        setFromDate(null);
        setToDate(null);
      } else {
        // Set dates from the selected period
        setFromDate(dayjs(selected.FromDate));
        setToDate(dayjs(selected.Todate));
      }
    }
  };

  const handleFilter = async () => {
    // Validation
    if (!selectedPeriod) {
      messageApi.warning("Please select a period");
      return;
    }

    if (!fromDate || !toDate) {
      messageApi.warning("Please select both From Date and To Date");
      return;
    }

    try {
      setFilterLoading(true);
      const fromDateStr = fromDate.format("YYYY-MM-DD");
      const toDateStr = toDate.format("YYYY-MM-DD");
      await getAllPos(fromDateStr, toDateStr, status);
      messageApi.success("Data retrieved successfully");
    } catch (error) {
      console.error("Error fetching IRN list:", error);
      messageApi.error("An error occurred while fetching data");
    } finally {
      setFilterLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedPeriod(null);
    setFromDate(null);
    setToDate(null);
    setStatus("All");
  };

  const isCustomPeriod = () => {
    const selected = periods.find((p) => p.PeriodId === selectedPeriod);
    return selected && selected.PeriodName === "Custom";
  };

  if (loading) {
    return (
      <div
        style={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Flex align="center" gap="middle">
          <Spin size="large" />
        </Flex>
      </div>
    );
  }

  return (
    <>
      {contextHolder}
      <Row>
        <Col xl={2} md={6}>
          <CustomLabel children={"Period"} isImportnt={false} />
          <Select
            id="period"
            placeholder="Select Period"
            allowClear
            showSearch
            optionFilterProp="label"
            size="medium"
            style={{ width: "100%" }}
            value={selectedPeriod}
            onChange={handlePeriodChange}
            options={periods.map((period) => ({
              value: period.PeriodId,
              label: period.PeriodName,
            }))}
          />
        </Col>

        <Col xl={2} md={6}>
          <CustomLabel children={"From Date"} isImportnt={false} />
          <DatePicker
            format="DD-MMM-YYYY"
            placeholder="Select a date"
            style={{ width: "100%" }}
            size="medium"
            value={fromDate}
            onChange={(date) => setFromDate(date)}
            disabled={!isCustomPeriod() && selectedPeriod !== null}
          />
        </Col>

        <Col xl={2} md={6}>
          <CustomLabel children={"To Date"} isImportnt={false} />
          <DatePicker
            format="DD-MMM-YYYY"
            placeholder="Select a date"
            style={{ width: "100%" }}
            size="medium"
            value={toDate}
            onChange={(date) => setToDate(date)}
            disabled={!isCustomPeriod() && selectedPeriod !== null}
          />
        </Col>
        <Col xl={2} md={6}>
          <CustomLabel children={"IRN Status"} isImportnt={false} />
          <Select
            id="status"
            placeholder="Select Status"
            allowClear
            showSearch
            optionFilterProp="label"
            size="medium"
            style={{ width: "100%" }}
            value={status}
            onChange={(value) => setStatus(value)}
            options={irnStatus.map((s) => ({
              value: s,
              label: s,
            }))}
          />
        </Col>

        <Col xl={4} style={{ textAlign: "center", marginTop: "1rem" }}>
          <Button
            type="primary"
            icon={<FilterOutlined />}
            size="large"
            onClick={handleFilter}
            loading={filterLoading}
            style={{
              marginRight: "0.75rem",
              backgroundColor: "#1aa8c4ff",
              borderColor: "#1a94c4ff",
              boxShadow: "0 2px 6px rgba(2, 2, 65, 0.3)",
            }}
          >
            Filter
          </Button>

          <Button
            icon={<ReloadOutlined />}
            size="large"
            onClick={handleReset}
            style={{
              marginRight: "0.75rem",
              borderColor: "#ff4d4f",
              color: "#ff4d4f",
            }}
          >
            Reset
          </Button>
        </Col>
      </Row>
    </>
  );
};
